import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { FormGroup } from 'react-bootstrap'
import { useTrackingTrigger } from '@indeed/flex-tracking-context'

import config from 'syft-acp-core/config'
import { Modal } from 'syft-acp-core/components/Modal'
import { getModal$ } from 'syft-acp-core/store/modals/selectors'
import { hideModal, showConfirmModal, showModal } from 'syft-acp-core/store/modals/actions'
import { cancelBookingsForWorker } from 'syft-acp-core/actions/bookings'
import { bulkEditAdminTimesheets } from 'syft-acp-core/api/resources/admin/timesheets'
import { fetchCancellableBookings } from 'syft-acp-core/api/resources/bookings'
import { fetchAdminBookings } from 'syft-acp-core/store/listings/actions'

import { trackingEvents } from './BulkCancelBookingModal.tracking'
import { ModalBody } from './ModalBody'
import {
  buildCancelPayload,
  buildCompensationDecisions,
  buildCoverPreview,
  flexerTermsURLFor,
  hasCompensationChanges,
  isOtherReason,
  loadCancellableBookings,
  loadOverbookCover,
  MODAL_NAME,
  reasonLabelFor,
  submitLabel,
  toggleInList,
} from './helpers'
import {
  BookingCompensationDecisions,
  BulkCancelBookingModalProps,
  CancelReason,
  CancelStep,
  CompensationSaveStatus,
  FlexCompReasonResult,
  ShiftOverbookCover,
  SpotDecision,
  WorkerJobBooking,
} from './types'
import './index.css'

export const BulkCancelBookingModal = ({ dispatch, item }: BulkCancelBookingModalProps) => {
  const { worker = {}, jobID, currentShiftID, isBlockBooking = false } = item.options
  const isShown = item.isShown
  const flexerTermsURL = flexerTermsURLFor(config.region)

  // Step 1 picks the shifts + reason; step 2 is the destructive spot decision and
  // final confirmation — kept separate so it isn't glossed over.
  const [step, setStep] = useState<CancelStep>('select')
  const [bookings, setBookings] = useState<WorkerJobBooking[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<number[]>([])
  const [reason, setReason] = useState<CancelReason>(null)
  const [otherReason, setOtherReason] = useState<string | undefined>(undefined)
  const [compensationDecisions, setCompensationDecisions] = useState<BookingCompensationDecisions>({})
  const [savedCompensationDecisions, setSavedCompensationDecisions] = useState<BookingCompensationDecisions>(
    {},
  )
  const [compensationSaveStatus, setCompensationSaveStatus] = useState<CompensationSaveStatus>(
    CompensationSaveStatus.Idle,
  )
  const [compensationSaveError, setCompensationSaveError] = useState<string | undefined>(undefined)
  const [compensationSavedThisSession, setCompensationSavedThisSession] = useState(false)
  // Keyed by shift_id. Shows who's first in line on a selected shift's overbook, if
  // anyone — drives whether "overbook" is offered as a spot decision in step 2, and
  // the per-date breakdown when it's chosen. Never blocks or changes what gets
  // submitted on its own; see STSMPD-1854.
  const [overbookCover, setOverbookCover] = useState<Record<number, ShiftOverbookCover>>({})
  // What happens to the vacated spot — set to a sensible default on entering step 2.
  const [spotDecision, setSpotDecision] = useState<SpotDecision>('keep')
  const triggerEvent = useTrackingTrigger()

  // Resets every choice made in the dialog, leaving the loaded bookings alone.
  const resetDecisions = useCallback(() => {
    setStep('select')
    setReason(null)
    setOtherReason(undefined)
    setCompensationDecisions({})
    setSavedCompensationDecisions({})
    setCompensationSaveStatus(CompensationSaveStatus.Idle)
    setCompensationSaveError(undefined)
    setCompensationSavedThisSession(false)
    setSpotDecision('keep')
  }, [])

  const setLoadedBookings = useCallback((loadedBookings: WorkerJobBooking[]) => {
    const decisions = buildCompensationDecisions(loadedBookings, null)
    setBookings(loadedBookings)
    setCompensationDecisions(decisions)
    setSavedCompensationDecisions(decisions)
  }, [])

  // Reruns whenever the target worker/job/shift changes while the modal stays mounted.
  useEffect(
    () =>
      loadCancellableBookings({
        isShown,
        workerID: worker.id,
        jobID,
        currentShiftID,
        resetDecisions,
        setBookings: setLoadedBookings,
        setSelected,
        setLoading,
        setLoadError,
      }),
    [isShown, worker.id, jobID, currentShiftID, resetDecisions, setLoadedBookings],
  )

  // Mirrors flex-core's own promotion gate (MoveOverbookWorkerToParent#call:
  // `return [] unless job.bookable_individually`). A block booking would leave a partial
  // booking if promoted, so the dialog must not offer it as an outcome — and needn't even
  // look it up. Multi-shift bookable-individually jobs ARE eligible.
  const promotionEligible = !isBlockBooking

  // Keyed off a joined string rather than the `selected` array reference so toggling
  // doesn't re-fire this on every render.
  const selectedKey = selected.join(',')
  useEffect(
    () => loadOverbookCover({ isShown: isShown && promotionEligible, shiftIDs: selected, setOverbookCover }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isShown, promotionEligible, selectedKey],
  )

  const resetState = useCallback(() => {
    resetDecisions()
    setSelected([])
    setBookings([])
    setLoadError(undefined)
    setOverbookCover({})
  }, [resetDecisions])

  const performClose = useCallback(() => {
    dispatch(hideModal(item.modalName))
    resetState()
  }, [dispatch, item.modalName, resetState])

  const closeModal = useCallback(() => {
    if (!compensationSavedThisSession) {
      performClose()

      return
    }
    dispatch(
      showConfirmModal({
        modalHeader: 'Leave without cancelling the worker?',
        modalCancel: 'Stay and continue',
        modalCancelKind: 'primary',
        modalCancelAutoFocus: true,
        modalConfirm: 'Close anyway',
        modalConfirmKind: 'danger',
        question:
          'Compensation changes are saved, but the worker is still booked. Closing now leaves cancellation incomplete.',
        onConfirm: performClose,
      }),
    )
  }, [compensationSavedThisSession, dispatch, performClose])

  const toggleShift = (id: number) => setSelected(prev => toggleInList(prev, id))

  const allIds = useMemo(() => bookings.map(b => b.shift_id), [bookings])
  const allSelected = selected.length === allIds.length && allIds.length > 0
  const toggleAll = () => setSelected(allSelected ? [] : allIds)

  const reasonLabel = reasonLabelFor(reason)

  const otherValid = isOtherReason(reasonLabel ?? reason) ? !!otherReason : true
  const canGoNext = selected.length > 0 && !!reason && otherValid && !loading

  const changeReason = useCallback(
    (value: string) => {
      setReason(value)
      setCompensationSaveStatus(CompensationSaveStatus.Idle)
      setCompensationSaveError(undefined)
      // A CH choice is made for the selected cancellation reason. Recalculate CH when
      // that reason changes so the new default is reviewed and overridden again if needed.
      const defaults = buildCompensationDecisions(bookings, value)
      setCompensationDecisions(previous =>
        Object.fromEntries(
          Object.entries(defaults).map(([shiftID, decision]) => [
            shiftID,
            {
              ...decision,
              fc: previous[Number(shiftID)]?.fc ?? decision.fc,
              fcReason: previous[Number(shiftID)]?.fcReason,
              fcApprover: previous[Number(shiftID)]?.fcApprover,
            },
          ]),
        ),
      )
    },
    [bookings],
  )

  const changeCompensationHours = useCallback(
    (booking: WorkerJobBooking, enabled: boolean) => {
      setCompensationSaveStatus(CompensationSaveStatus.Idle)
      setCompensationSaveError(undefined)
      if (enabled) {
        setCompensationDecisions(previous => ({
          ...previous,
          [booking.shift_id]: { ...previous[booking.shift_id], ch: true, chDefaulted: false },
        }))

        return
      }
      // The warning explains an automatic policy choice. Existing or manually selected
      // CH was not chosen by that rule, so it can be unchecked without this warning.
      if (!compensationDecisions[booking.shift_id]?.chDefaulted) {
        setCompensationDecisions(previous => ({
          ...previous,
          [booking.shift_id]: { ...previous[booking.shift_id], ch: false, chDefaulted: false },
        }))

        return
      }
      dispatch(
        showConfirmModal({
          modalHeader: 'Remove Compensation Hours (CH)?',
          modalCancel: 'Keep CH',
          modalCancelKind: 'primary',
          modalCancelAutoFocus: true,
          modalConfirm: 'Remove CH anyway',
          modalConfirmKind: 'danger',
          question: (
            <div>
              <p>
                Compensation Hours (CH) was automatically selected because this shift starts within 24 hours
                and the cancellation reason is <strong>“{reasonLabel ?? reason}”</strong>.
              </p>
              <p>
                Indeed Flex policy applies minimum-hours compensation for this cancellation reason. The
                applicable{' '}
                <a
                  href={flexerTermsURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Flexer Terms and Conditions (opens in a new tab)"
                >
                  Flexer Terms and Conditions
                </a>{' '}
                describe compensation for client cancellations within 24 hours.
              </p>
              <p>
                If this cancellation is actually due to worker performance, attendance, or another
                worker-related reason, return to the cancellation form and choose the correct reason.
              </p>
              <p>
                <strong>Remove CH only for an approved exception.</strong>
              </p>
            </div>
          ),
          onConfirm: () =>
            setCompensationDecisions(previous => ({
              ...previous,
              [booking.shift_id]: { ...previous[booking.shift_id], ch: false, chDefaulted: false },
            })),
        }),
      )
    },
    [compensationDecisions, dispatch, flexerTermsURL, reason, reasonLabel],
  )

  const changeFlexCompensation = useCallback(
    (booking: WorkerJobBooking, enabled: boolean) => {
      setCompensationSaveStatus(CompensationSaveStatus.Idle)
      setCompensationSaveError(undefined)
      if (!enabled) {
        setCompensationDecisions(previous => ({
          ...previous,
          [booking.shift_id]: {
            ...previous[booking.shift_id],
            fc: false,
            fcReason: undefined,
            fcApprover: undefined,
          },
        }))

        return
      }
      // FC needs a reason and approver, so leave the checkbox off until that form
      // is completed.
      dispatch(
        showModal(
          'addFlexCompReasonModal',
          {
            shiftBooking: {
              id: `shift ${booking.shift_id}`,
              worker,
              shift: { start_time: booking.start_time },
            },
          },
          ((data?: FlexCompReasonResult) => {
            const hasRequiredDetails =
              data?.confirmed &&
              data.syftCompReason &&
              data.approver &&
              (data.syftCompReason !== 'Other' || data.otherReason)
            if (!hasRequiredDetails) return
            setCompensationDecisions(previous => ({
              ...previous,
              [booking.shift_id]: {
                ...previous[booking.shift_id],
                fc: true,
                fcReason: data.otherReason ? `Other: ${data.otherReason}` : data.syftCompReason,
                fcApprover: data.approver,
              },
            }))
          }) as () => void,
        ),
      )
    },
    [dispatch, worker],
  )

  const coverPreview = useMemo(
    () => buildCoverPreview(selected, bookings, overbookCover),
    [selected, bookings, overbookCover],
  )

  // Does at least one selected shift have someone first in line to move in? No listing,
  // or an empty one, means there's nobody to promote — which is what decides whether
  // keeping the spot is described as an overbook fill or as a plain reopen.
  const coveredCount = useMemo(() => coverPreview.filter(({ cover }) => cover.covered).length, [coverPreview])
  const hasOverbookCover = coveredCount > 0

  // Whether an overbook fill was even on offer when a decision was made — without it,
  // "reopen the offering" is ambiguous in analytics: it can mean ops rejected an available
  // overbook, or that there was nothing to reject. `overbook_promotion_eligible` says
  // whether flex-core could have promoted at all (single-shift job), and
  // `overbook_cover_resolved_shifts` separates a genuine "no cover" from an unknown one
  // (cover fetch failed, still in flight, or skipped as ineligible).
  const overbookAvailability = useMemo(
    () => ({
      overbook_available: hasOverbookCover,
      overbook_promotion_eligible: promotionEligible,
      overbook_covered_shifts: coveredCount,
      overbook_cover_resolved_shifts: coverPreview.length,
      selected_shift_count: selected.length,
    }),
    [hasOverbookCover, promotionEligible, coveredCount, coverPreview.length, selected.length],
  )

  // Tracks every spot decision the user actively picks on step 2. The default is set by
  // goToConfirm rather than a click, so accepting it produces no event here — the final
  // choice is always on the save event too.
  const selectSpotDecision = useCallback(
    (decision: SpotDecision) => {
      setSpotDecision(decision)
      triggerEvent(trackingEvents.BULK_CANCEL_BOOKING.SPOT_DECISION.SELECTED, {
        spot_decision: decision,
        ...overbookAvailability,
      })
    },
    [triggerEvent, overbookAvailability],
  )

  const goToConfirm = useCallback(() => {
    setSpotDecision('keep')
    setStep('confirm')
  }, [])

  const compensationUpdates = useMemo(
    () =>
      selected
        .map(shiftID => {
          const decision = compensationDecisions[shiftID]

          return decision
            ? {
                shift_id: shiftID,
                syft_unpaid_hours_compensation: decision.ch,
                syft_compensation: decision.fc,
                ...(decision.fcReason ? { syft_compensation_reason: decision.fcReason } : {}),
                ...(decision.fcApprover ? { syft_compensation_approver: decision.fcApprover } : {}),
              }
            : null
        })
        .filter((update): update is NonNullable<typeof update> => update !== null),
    [compensationDecisions, selected],
  )

  const compensationHasChanges = useMemo(
    () => hasCompensationChanges(selected, compensationDecisions, savedCompensationDecisions),
    [compensationDecisions, savedCompensationDecisions, selected],
  )

  const saveCompensation = useCallback(async () => {
    if (!compensationHasChanges) return
    setCompensationSaveStatus(CompensationSaveStatus.Saving)
    setCompensationSaveError(undefined)
    const entries = compensationUpdates.map(update => ({ ...update, worker_id: worker.id }))

    try {
      await bulkEditAdminTimesheets({ payload: { entries } })
    } catch {
      try {
        const response = await fetchCancellableBookings({ workerID: worker.id, jobID })
        const reloadedBookings: WorkerJobBooking[] = Array.isArray(response?.payload) ? response.payload : []
        const decisions = buildCompensationDecisions(reloadedBookings, null)
        const currentBooking = reloadedBookings.find(
          booking => Number(booking.shift_id) === Number(currentShiftID),
        )

        // The bulk edit may save some shifts before another fails. Reload and reset the
        // form so the next attempt starts from server state, not mixed saved and unsaved choices.
        setBookings(reloadedBookings)
        setSelected(currentBooking ? [currentBooking.shift_id] : [])
        setReason(null)
        setOtherReason(undefined)
        setCompensationDecisions(decisions)
        setSavedCompensationDecisions(decisions)
        setSpotDecision('keep')
      } catch {
        setCompensationSaveError(
          'We could not reload the current compensation values. Close and reopen the dialog before retrying.',
        )
        setCompensationSaveStatus(CompensationSaveStatus.Error)

        return
      }
      setCompensationSaveError(
        'Some compensation changes may have saved. We reloaded the current values; review and try again. Nothing was cancelled.',
      )
      setCompensationSaveStatus(CompensationSaveStatus.Error)

      return
    } finally {
      // The modal saves through the API directly, so refresh the Redux entities that
      // render the bookings table behind it. Otherwise the persisted CH/FC values look stale.
      dispatch(fetchAdminBookings(currentShiftID, jobID))
    }

    setSavedCompensationDecisions(previous => {
      const next = { ...previous }
      selected.forEach(shiftID => {
        if (compensationDecisions[shiftID]) {
          next[shiftID] = { ...compensationDecisions[shiftID] }
        }
      })

      return next
    })
    setCompensationSaveStatus(CompensationSaveStatus.Saved)
    setCompensationSavedThisSession(true)
  }, [
    compensationDecisions,
    compensationHasChanges,
    compensationUpdates,
    currentShiftID,
    dispatch,
    jobID,
    selected,
    worker.id,
  ])

  // A single /bookings/delete call — `delete_spot` tells flex-core whether to reopen the
  // shift (and let it promote an overbook backup) or shrink/remove the spot instead. Must
  // stay one call: doing this as delete-then-remove_spot races the async overbook-
  // promotion reaction the reopen triggers (STSMPD-2072).
  const onConfirm = useCallback(() => {
    const payload = buildCancelPayload({
      workerID: worker.id,
      jobID,
      shiftIDs: selected,
      currentShiftID,
      reason,
      reasonLabel,
      otherReason,
      deleteSpot: spotDecision === 'delete',
    })
    // Free text, FC reasons, and approver names can contain personal information.
    // Analytics receives only counts and other structured fields.
    const { user_provided_custom_reason: _omitCustomReason, ...trackedPayload } = payload
    const chShiftCount = compensationUpdates.filter(update => update.syft_unpaid_hours_compensation).length
    const fcShiftCount = compensationUpdates.filter(update => update.syft_compensation).length
    triggerEvent(trackingEvents.BULK_CANCEL_BOOKING.SAVE.CLICKED, {
      ...trackedPayload,
      spot_decision: spotDecision,
      compensation_hours_shift_count: chShiftCount,
      flex_compensation_shift_count: fcShiftCount,
      ...overbookAvailability,
    })
    dispatch(cancelBookingsForWorker(payload))
    performClose()
  }, [
    selected,
    reason,
    reasonLabel,
    otherReason,
    compensationUpdates,
    spotDecision,
    overbookAvailability,
    worker,
    jobID,
    currentShiftID,
    performClose,
    dispatch,
    triggerEvent,
  ])

  const count = selected.length

  return (
    <Modal
      className="bulk-cancel-modal"
      header={`Cancel ${worker.first_name || ''} ${worker.last_name || ''}`.trim()}
      isShown={isShown}
      isLoading={loading || compensationSaveStatus === CompensationSaveStatus.Saving}
      onClose={closeModal}
      onConfirm={step === 'select' ? goToConfirm : onConfirm}
      canSubmit={
        step === 'select'
          ? canGoNext && !compensationHasChanges && compensationSaveStatus !== CompensationSaveStatus.Saving
          : true
      }
      confirmKind={step === 'confirm' && spotDecision === 'delete' ? 'danger' : 'primary'}
      confirmationText={submitLabel({
        step,
        count,
        spotDecision,
      })}
    >
      <FormGroup className="form-item">
        <ModalBody
          loadError={loadError}
          loading={loading}
          step={step}
          selectStepProps={{
            worker,
            isBlockBooking,
            bookings,
            allSelected,
            toggleAll,
            selected,
            toggleShift,
            compensationDecisions,
            compensationHasChanges,
            compensationSaveStatus,
            compensationSaveError,
            saveCompensation,
            changeCompensationHours,
            changeFlexCompensation,
            reason,
            reasonLabel,
            setReason: changeReason,
            otherReason,
            setOtherReason,
          }}
          spotDecisionStepProps={{
            count,
            worker,
            reason,
            reasonLabel,
            otherReason,
            spotDecision,
            setSpotDecision: selectSpotDecision,
            hasOverbookCover,
            coverPreview,
            onBack: () => setStep('select'),
          }}
        />
      </FormGroup>
    </Modal>
  )
}

export default connect((state: any) => ({
  item: getModal$(state.modals)(MODAL_NAME),
  // @ts-expect-error getModal$ returns the generic ModalsItem; options is narrowed to
  // BulkCancelModalOptions once this modal is shown (see EndJobAssignmentModal).
}))(BulkCancelBookingModal)
